class BaskisizdeQuoteWidget {
  constructor(root) {
    this.root = root;
    this.apiBase = (root.dataset.apiBase || window.location.origin).replace(/\/$/, '');
    this.messages = {
      invalidFile: root.dataset.invalidFile || 'Lütfen .stl uzantılı bir dosya seçin.',
      fileRequired: root.dataset.fileRequired || 'Önce bir STL dosyası seçin.',
      fileReadError: root.dataset.fileReadError || 'Dosya okunamadı.',
      quoteLoading: root.dataset.quoteLoading || 'Model analiz ediliyor…',
      quoteError: root.dataset.quoteError || 'Fiyat hesaplanamadı.',
      calculateLabel: root.dataset.calculateLabel || 'Anında Fiyat Hesapla',
      checkoutLoading: root.dataset.checkoutLoading || 'Shopier bağlantısı hazırlanıyor…',
      checkoutError: root.dataset.checkoutError || 'Ödeme bağlantısı oluşturulamadı.',
      checkoutLabel: root.dataset.checkoutLabel || "Shopier'de Güvenli Öde",
      defaultColor: root.dataset.defaultColor || 'Beyaz'
    };
    this.file = null;
    this.quote = null;
    this.fileInput = root.querySelector('[data-file]');
    this.drop = root.querySelector('[data-drop]');
    this.fileName = root.querySelector('[data-file-name]');
    this.form = root.querySelector('[data-form]');
    this.submit = root.querySelector('[data-submit]');
    this.checkout = root.querySelector('[data-checkout]');
    this.error = root.querySelector('[data-error]');
    this.result = root.querySelector('[data-result]');
    this.bind();
  }

  bind() {
    this.drop.addEventListener('click', () => this.fileInput.click());
    this.drop.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.fileInput.click();
      }
    });
    this.fileInput.addEventListener('change', () => this.setFile(this.fileInput.files[0]));
    for (const event of ['dragenter', 'dragover']) {
      this.drop.addEventListener(event, (e) => { e.preventDefault(); this.drop.classList.add('is-dragging'); });
    }
    for (const event of ['dragleave', 'drop']) {
      this.drop.addEventListener(event, (e) => { e.preventDefault(); this.drop.classList.remove('is-dragging'); });
    }
    this.drop.addEventListener('drop', (e) => this.setFile(e.dataTransfer.files[0]));
    this.form.addEventListener('submit', (e) => { e.preventDefault(); this.requestQuote(); });
    this.checkout.addEventListener('click', () => this.createCheckout());
  }

  setFile(file) {
    this.clearError();
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.stl')) return this.showError(this.messages.invalidFile);
    this.file = file;
    this.fileName.textContent = `${file.name} · ${this.formatBytes(file.size)}`;
    this.submit.disabled = false;
    this.quote = null;
    this.result.classList.add('bsk-hidden');
  }

  async requestQuote() {
    if (!this.file) return this.showError(this.messages.fileRequired);
    this.clearError();
    this.submit.disabled = true;
    this.submit.textContent = this.messages.quoteLoading;
    try {
      const dataBase64 = await this.toBase64(this.file);
      const payload = {
        filename: this.file.name,
        dataBase64,
        material: this.root.querySelector('[name=material]').value,
        color: this.root.querySelector('[name=color]').value,
        quantity: Number(this.root.querySelector('[name=quantity]').value),
        supports: this.root.querySelector('[name=supports]').checked
      };
      const response = await fetch(`${this.apiBase}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const body = await this.readJson(response);
      if (!response.ok) throw new Error(body.error || this.messages.quoteError);
      this.quote = body;
      this.renderQuote(body);
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.submit.disabled = false;
      this.submit.textContent = this.messages.calculateLabel;
    }
  }

  renderQuote(quote) {
    const analysis = quote.analysis;
    const price = quote.price;
    this.root.querySelector('[data-price]').textContent = `${price.totalTl.toLocaleString('tr-TR')} TL`;
    this.root.querySelector('[data-quote-id]').textContent = quote.id;
    const colorOutput = this.root.querySelector('[data-color]');
    if (colorOutput) colorOutput.textContent = quote.color || this.messages.defaultColor;
    this.root.querySelector('[data-dimensions]').textContent = `${analysis.dimensionsMm.x} × ${analysis.dimensionsMm.y} × ${analysis.dimensionsMm.z} mm`;
    this.root.querySelector('[data-grams]').textContent = `${price.estimatedGramsTotal} g`;
    this.root.querySelector('[data-hours]').textContent = `${price.estimatedHoursTotal} saat`;
    this.root.querySelector('[data-expiry]').textContent = new Date(quote.expiresAt).toLocaleString('tr-TR');
    this.checkout.textContent = `${this.messages.checkoutLabel} – ${price.totalTl.toLocaleString('tr-TR')} TL`;
    this.result.classList.remove('bsk-hidden');
    this.result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  async createCheckout() {
    if (!this.quote) return;
    this.clearError();
    this.checkout.disabled = true;
    this.checkout.textContent = this.messages.checkoutLoading;
    try {
      const response = await fetch(`${this.apiBase}/api/quotes/${encodeURIComponent(this.quote.id)}/checkout`, { method: 'POST' });
      const body = await this.readJson(response);
      if (!response.ok) throw new Error(body.error || this.messages.checkoutError);
      window.location.assign(body.checkoutUrl);
    } catch (error) {
      this.showError(error.message);
      this.checkout.disabled = false;
      this.checkout.textContent = `${this.messages.checkoutLabel} – ${this.quote.price.totalTl.toLocaleString('tr-TR')} TL`;
    }
  }

  async readJson(response) {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      throw new Error(response.ok ? 'Sunucudan geçersiz yanıt alındı.' : `${response.status} ${response.statusText}`);
    }
  }

  toBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1]);
      reader.onerror = () => reject(new Error(this.messages.fileReadError));
      reader.readAsDataURL(file);
    });
  }

  formatBytes(bytes) {
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  showError(message) {
    this.error.textContent = message;
    this.error.classList.remove('bsk-hidden');
  }

  clearError() {
    this.error.textContent = '';
    this.error.classList.add('bsk-hidden');
  }
}

document.querySelectorAll('[data-bsk-quote]').forEach((root) => new BaskisizdeQuoteWidget(root));